import { Node } from '@tiptap/core';
import { Goal } from '@app/models/goals/goal.model';
import { GoalStatus } from '@app/models/goals/goal-status.model';
import moment from 'moment';

export type FetchGoalFn = (id: number) => Promise<Goal>;

function getStatusClass(goal: Goal): string {
  if (goal.archived) return 'status gray';
  if (goal.complete) return 'status blue';
  switch (goal.status) {
    case GoalStatus.OFF_TRACK: return 'status red';
    case GoalStatus.PROGRESSING: return 'status yellow';
    case GoalStatus.ON_TRACK: return 'status green';
    default: return '';
  }
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function renderGoal(dom: HTMLElement, goal: Goal): void {
  const date = moment(goal.endDate).format('D MMM YYYY');
  const pct = goal.completionPercentage ?? 0;
  dom.innerHTML = `
    <span class="rl-data">
      <span class="${getStatusClass(goal)}">&nbsp;</span>
      <span class="title">${escapeHtml(goal.title)}</span>
      <span class="pill yellow">${date}</span>
      <span class="progress-bar-container">
        <span class="progress-bar-line" style="width: ${pct}%">&nbsp;</span>
        <span class="progress-bar-text" style="left: ${pct}%">${pct}%</span>
      </span>
    </span>`;
}

function renderLoading(dom: HTMLElement): void {
  dom.innerHTML = `<span class="rl-data"><span class="rich-link-loading"><i class="fas fa-spinner fa-spin"></i>&nbsp;Loading&hellip;</span></span>`;
}

function renderError(dom: HTMLElement): void {
  dom.innerHTML = `<span class="rl-data"><span class="rich-link-error"><i class="fas fa-times"></i>&nbsp;Failed to load goal</span></span>`;
}

export function preprocessRichLinks(html: string): string {
  return html.replace(
    /<strong>{{GOALS\|(\d+)}}<\/strong>/g,
    (_, id) => `<span data-frankli-link-type="GOALS" data-id="${id}"></span>`
  );
}

export function createRichLinkExtension(fetchGoalFn: FetchGoalFn) {
  const goalCache = new Map<number, Goal>();

  async function fetchGoal(id: number): Promise<Goal> {
    if (goalCache.has(id)) {
      return goalCache.get(id)!;
    }
    const goal = await fetchGoalFn(id);
    goalCache.set(id, goal);
    return goal;
  }

  return Node.create({
    name: 'richLink',
    group: 'inline',
    inline: true,
    atom: true,

    addAttributes() {
      return {
        feature: { default: 'GOALS' },
        id: { default: null },
      };
    },

    parseHTML() {
      return [
        {
          tag: 'span[data-frankli-link-type]',
          getAttrs: element => {
            const el = element as HTMLElement;
            const id = parseInt(el.getAttribute('data-id') ?? '0', 10);
            return isNaN(id) || id === 0
              ? false
              : { feature: el.getAttribute('data-frankli-link-type'), id };
          },
        },
      ];
    },

    renderHTML({ node }) {
      return ['strong', {}, `{{${node.attrs['feature']}|${node.attrs['id']}}}`];
    },

    addNodeView() {
      return ({ node }: any) => {
        const dom = document.createElement('span');
        dom.className = 'rich-link';
        dom.contentEditable = 'false';
        dom.draggable = true;
        renderLoading(dom);
        fetchGoal(node.attrs['id'])
          .then(goal => renderGoal(dom, goal))
          .catch(() => renderError(dom));
        return { dom };
      };
    },
  });
}
